import type { PloomesDealFilters } from './types';

export const FILTER_FIELD_LABELS: Record<string, string> = {
    search: 'Busca',
    defaulted_only: 'Inadimplência',
    open_installments_only: 'Parcelas em aberto',
    owner_names: 'Responsável',
    segment: 'Segmento',
    client_classifications: 'Classificação',
    finish_date_from: 'Ganho',
    finish_date_to: 'Ganho',
    amount_min: 'Valor mín.',
    amount_max: 'Valor máx.',
    buckler_one: 'Buckler One',
    team_agreement: 'Team Agreement',
    commission_exception: 'Exceção Ploomes',
    has_sdr: 'Tem SDR',
};

export const SORT_OPTIONS = [
    { value: '-finish_date', label: 'Ganho — mais recente' },
    { value: 'finish_date', label: 'Ganho — mais antigo' },
    { value: '-amount', label: 'Valor — maior' },
    { value: 'amount', label: 'Valor — menor' },
    { value: 'title', label: 'Título A–Z' },
    { value: '-title', label: 'Título Z–A' },
    { value: 'contact_name', label: 'Cliente A–Z' },
    { value: '-contact_name', label: 'Cliente Z–A' },
    { value: 'contact_state', label: 'UF A–Z' },
    { value: '-contact_state', label: 'UF Z–A' },
    { value: 'owner_name', label: 'Responsável A–Z' },
    { value: '-owner_name', label: 'Responsável Z–A' },
    { value: 'internal_number', label: 'Nº Pedido A–Z' },
    { value: '-internal_number', label: 'Nº Pedido Z–A' },
    { value: '-id', label: 'ID — mais recente' },
] as const;

export const PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

/** Filtros removidos da UI — não contam em chips/contador. */
const IGNORED_KEYS = new Set([
    'page',
    'per_page',
    'sort',
    'pipeline_name',
    'stage_name',
    'status_name',
    'account_executive',
    'updated_from',
    'updated_to',
]);

export function countActiveFilters(filters: PloomesDealFilters): number {
    return (Object.keys(filters) as Array<keyof PloomesDealFilters>).filter((key) => {
        if (IGNORED_KEYS.has(key)) return false;
        if (key === 'finish_date_to' && filters.finish_date_from) return false;
        const v = filters[key];
        if (Array.isArray(v)) return v.length > 0;
        return v !== '' && v !== undefined;
    }).length;
}

export function listActiveFilters(filters: PloomesDealFilters): Array<{ key: keyof PloomesDealFilters; label: string; value: string }> {
    const keys = (Object.keys(filters) as Array<keyof PloomesDealFilters>).filter((key) => {
        if (IGNORED_KEYS.has(key)) return false;
        if (key === 'finish_date_to' && filters.finish_date_from) return false;
        const v = filters[key];
        if (Array.isArray(v)) return v.length > 0;
        return v !== '' && v !== undefined;
    });

    return keys.map((key) => {
        if (key === 'finish_date_from') {
            const from = filters.finish_date_from;
            const to = filters.finish_date_to;
            const fromLabel = formatFilterChipValue('finish_date_from', from);
            const toLabel = to ? formatFilterChipValue('finish_date_to', to) : '…';
            return {
                key,
                label: 'Ganho',
                value: `${fromLabel} — ${toLabel}`,
            };
        }

        return {
            key,
            label: FILTER_FIELD_LABELS[key] ?? key,
            value: Array.isArray(filters[key])
                ? (filters[key] as string[]).join(', ')
                : String(filters[key]),
        };
    });
}

export function formatFilterChipValue(key: keyof PloomesDealFilters, value: string): string {
    if (key === 'amount_min' || key === 'amount_max') {
        const n = Number(value);
        if (!Number.isNaN(n)) {
            return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
        }
    }
    if (key.endsWith('_from') || key.endsWith('_to')) {
        const [y, m, d] = value.split('-');
        if (y && m && d) return `${d}/${m}/${y}`;
    }
    if (key === 'buckler_one') {
        return 'Sim';
    }
    if (key === 'defaulted_only') {
        return 'Somente inadimplentes';
    }
    if (key === 'open_installments_only') {
        return 'Somente com parcelas em aberto';
    }
    if (key === 'team_agreement' || key === 'commission_exception' || key === 'has_sdr') {
        return 'Sim';
    }
    if (key === 'owner_names' || key === 'client_classifications') {
        return value.length > 42 ? `${value.slice(0, 42)}…` : value;
    }
    return value.length > 28 ? `${value.slice(0, 28)}…` : value;
}
